import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
// Removed legacy MFAVerification in favor of new TOTP flow
import toast from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import { Eye, EyeOff, Mail, Lock, Shield, ArrowRight, Building2 } from 'lucide-react';
import { encryptData } from '../utils/encryption';

interface LoginProps {}

const Login: React.FC<LoginProps> = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mfaStage, setMfaStage] = useState<'none' | 'qr' | 'otp'>('none');
  const [mfaEmail, setMfaEmail] = useState<string>('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [otp, setOtp] = useState<string>('');
  const otpRefs = useRef<Array<HTMLInputElement | null>>([]);
  
  const navigate = useNavigate();
  const { setAuthenticated } = useAuth() as any;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      // Step 1: Verify email + password. If wrong, show error and stop.
      const payload = {
        email: data.email,
        password: data.password,
      }

      const credsCheck = await authAPI.login(encryptData(payload));
      if (!credsCheck?.success) {
        setIsLoading(false);
        errorModal(credsCheck?.message || 'Wrong credentials');
        return;
      }

      // Step 2: Initiate TOTP flow via email-only endpoint
      const start = await authAPI.startEmailLogin(data.email);
      if (!start?.success || !start?.data?.requiresTotp) {
        setIsLoading(false);
        errorModal(start?.message || 'Failed to start MFA');
        return;
      }

      setMfaEmail(data.email);
      if (start.data.alreadySetup === false && start.data.qrCode) {
        setQrCode(start.data.qrCode);
        setMfaStage('qr');
      } else {
        setQrCode(null);
        setMfaStage('otp');
      }
      setIsLoading(false);
    } catch (error: any) {
      console.error('Login error:', error);
      const msg = error?.response?.data?.error || error?.response?.data?.message || error?.message;
      errorModal(msg || 'Login failed. Please check your credentials.');
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!mfaEmail || otp.trim().length !== 6) {
      errorModal('Enter the 6-digit code');
      return;
    }
    try {
      setIsLoading(true);
      const res = await authAPI.verifyTotp(mfaEmail, otp);
      if (res?.success && res?.data?.token && res?.data?.user) {
        if (typeof setAuthenticated === 'function') {
          setAuthenticated(res.data.user, res.data.token);
        } else {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('user', JSON.stringify(res.data.user));
          localStorage.setItem('permissions', res.data.user.invoice_permission);
        }
        toast.success('Login successful!');

        if (res.data.user.invoice_permission === 'admin' || res.data.user.invoice_permission === 'super_admin') {
          navigate('/dashboard');
        } else {
          navigate('/invoices');
        }
      } else {
        errorModal(res?.message || 'Invalid or expired code');
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 400) errorModal('Invalid or expired code');
      else if (status === 404) errorModal('User not found');
      else if (status === 401) errorModal('Account deactivated');
      else errorModal(error?.response?.data?.error || 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const setOtpAtIndex = (index: number, value: string) => {
    const digits = otp.split('');
    digits[index] = value;
    setOtp(digits.join(''));
  };

  const focusInput = (index: number) => {
    const el = otpRefs.current[index];
    if (el) el.focus();
  };

  const handleOtpChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    if (!digit) {
      setOtpAtIndex(index, '');
      return;
    }
    setOtpAtIndex(index, digit);
    if (index < 5) focusInput(index + 1);
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const currentVal = otp[index] || '';
      if (currentVal) {
        setOtpAtIndex(index, '');
        for (let i = index - 1; i >= 0; i--) {
          if ((otp[i] || '') !== '') {
            focusInput(i);
            return;
          }
        }
        focusInput(Math.max(0, index - 1));
      } else {
        focusInput(Math.max(0, index - 1));
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusInput(Math.max(0, index - 1));
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusInput(Math.min(5, index + 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (!isLoading) {
        handleVerifyOtp();
      }
    }
  };

  const handleOtpPaste = (index: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!text) return;
    const toFill = text.slice(0, 6 - index);
    let digits = otp.split('');
    for (let i = 0; i < toFill.length; i++) {
      digits[index + i] = toFill[i];
    }
    setOtp(digits.join(''));
    const nextIndex = Math.min(5, index + toFill.length);
    focusInput(nextIndex);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Logo and Header */}
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center shadow-lg">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Welcome back
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to your VMS account
            </p>
          </div>

          {/* Login / MFA Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            {mfaStage === 'none' && (
              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email.message?.toString()}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      },
                      pattern: {
                        value: /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/,
                        message: 'Password must contain at least 8 characters, 1 capital letter, and 1 special character'
                      }
                    })}
                    className="block w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password.message?.toString()}</p>
                )}
              </div>

              {/* Remember Me and Forgot Password */}
              <div className="flex items-center justify-end">
                {/* <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                    Remember me
                  </label>
                </div> */}
                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Sign in
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </button>
              </div>
              </form>
            )}

            {mfaStage === 'qr' && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                    <Shield className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan to set up Microsoft Authenticator</h3>
                  {qrCode && (
                    <img src={qrCode} alt="MFA QR Code" className="mx-auto border rounded-lg" />
                  )}
                  <p className="text-sm text-gray-600 mt-3">Scan the QR code in Microsoft Authenticator, then continue to enter the 6-digit code.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setMfaStage('otp')}
                  className="w-full bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Continue
                </button>
              </div>
            )}

            {mfaStage === 'otp' && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="mx-auto h-16 w-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                    <Shield className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Enter 6-digit code</h3>
                  <p className="text-sm text-gray-600 mb-4">From Microsoft Authenticator for {mfaEmail}</p>
                  <div className="flex justify-center space-x-2">
                    {[0,1,2,3,4,5].map((i) => (
                      <input
                        key={i}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={1}
                        value={otp[i] || ''}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        onPaste={(e) => handleOtpPaste(i, e)}
                        ref={(el) => (otpRefs.current[i] = el)}
                        className="w-12 h-12 text-center border border-gray-300 rounded-md text-lg font-semibold focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      />
                    ))}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setOtp('');
                      if (qrCode) setMfaStage('qr'); else setMfaStage('none');
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={isLoading}
                    className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Verifying...' : 'Verify'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Security Notice */}
          <div className="flex items-center justify-center text-xs text-gray-500">
            <Shield className="h-4 w-4 mr-1" />
            Protected by enterprise-grade security
          </div>
        </div>
      </div>

      {/* Right Side - Hero Section */}
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative h-full flex items-center justify-center px-12">
          <div className="text-center text-white">
            <div className="mb-8">
              <Building2 className="h-20 w-20 mx-auto mb-6 opacity-90" />
              <h1 className="text-4xl font-bold mb-4">
                Vendor Management System
              </h1>
              <p className="text-xl text-primary-100 mb-8 max-w-md">
                Streamline your vendor relationships, automate invoice processing, and ensure compliance with our comprehensive VMS platform.
              </p>
            </div>
            
            {/* Feature Highlights */}
            <div className="grid grid-cols-1 gap-6 max-w-lg mx-auto">
              <div className="flex items-center text-left">
                <div className="bg-white bg-opacity-20 rounded-full p-2 mr-4">
                  <Shield className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">HMRC Compliant</h3>
                  <p className="text-primary-100 text-sm">Full VAT compliance and reporting</p>
                </div>
              </div>
              <div className="flex items-center text-left">
                <div className="bg-white bg-opacity-20 rounded-full p-2 mr-4">
                  <Lock className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">Enterprise Security</h3>
                  <p className="text-primary-100 text-sm">MFA, audit trails, and role-based access</p>
                </div>
              </div>
              <div className="flex items-center text-left">
                <div className="bg-white bg-opacity-20 rounded-full p-2 mr-4">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">Smart Automation</h3>
                  <p className="text-primary-100 text-sm">OCR processing and workflow automation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>
          <div className="absolute top-1/2 left-10 w-16 h-16 bg-white bg-opacity-5 rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default Login; 